import * as classNames from "classnames";
import * as React from "react";
import { FadeInArray } from "../../../Shared/Animation/FadeInArray";
import Render from "../../../Shared/Render";
import * as styles from "./DailyGoals.css";
import { GetActiveDailyGoals } from "./GetActiveDailyGoals";
import { GetRealMomentum } from "./GetRealMomentum";
import { IDailyGoalsProps } from "./IDailyGoalsProps";
import { IDailyGoalsState } from "./IDailyGoalsState";
import { LogDailyGoals } from "./LogDailyGoals";
import { RenderDailyGoal } from "./RenderDailyGoal";
import { ToggleCheckedGoal } from "./ToggleCheckedGoal";
import { ToggleFutureDailyGoals } from "./ToggleFutureDailyGoals";

export class DailyGoals extends React.Component<
    IDailyGoalsProps,
    IDailyGoalsState
> {
    private readonly loadingSkeletonCount = 6;

    constructor(props: IDailyGoalsProps) {
        super(props);

        this.state = {
            checkedGoals: [],
            isPosting: false,
            showFutureDailyGoals: false,
        };
    }

    render() {
        if (this.props.GoalsContainer.props.searchText !== "") {
            return null;
        }

        const loadStatus =
            this.props.GoalsContainer.state.goalDataLoadStatus;
        const isLoading = loadStatus === "loading";
        const hasLoadError = loadStatus === "error";

        const dailyGoals = GetActiveDailyGoals(this.props.GoalsContainer);

        if (!isLoading && !hasLoadError && !dailyGoals.length) {
            return null;
        }

        const sortedDailyGoals = dailyGoals.sort(
            (goalA, goalB) =>
                GetRealMomentum(goalA, this.props.GoalStreakInfos) -
                GetRealMomentum(goalB, this.props.GoalStreakInfos)
        );

        const sortedDailyGoalRows = sortedDailyGoals
            .map((goal) => ({
                goal,
                goalStreakInfo: this.props.GoalStreakInfos.find(
                    (gsi) => gsi.result_goalid === goal.id
                ),
            }))
            .filter(
                ({ goalStreakInfo }) =>
                    this.state.showFutureDailyGoals ||
                    !goalStreakInfo?.isFutureDailyGoal
            );

        return (
            <div className={styles.root}>
                <Render id="dailyGoalsLoadingSkeleton" when={isLoading}>
                    <DailyGoalsLoadingSkeleton
                        rowCount={this.loadingSkeletonCount}
                    />
                </Render>
                <Render id="dailyGoalsLoadError" when={hasLoadError}>
                    <div className={styles.loadError} role="status">
                        Unable to load daily goals.
                    </div>
                </Render>
                <Render id="dailyGoalsLoaded" when={!isLoading && !hasLoadError}>
                    <div
                        id="dailyGoalsList"
                        className={classNames(styles.list, {
                            [styles.isPosting]: this.state.isPosting,
                        })}
                    >
                        <FadeInArray
                            label="DailyGoals"
                            array={sortedDailyGoalRows}
                            itemDelayMs={100}
                            containerClass={styles.fadeInList}
                            render={({ goal, goalStreakInfo }) => (
                                <RenderDailyGoal
                                    ToggleCheckbox={ToggleCheckedGoal.bind(
                                        this
                                    )}
                                    isChecked={this.state.checkedGoals.includes(
                                        goal.name
                                    )}
                                    goal={goal}
                                    goalStreakInfo={goalStreakInfo}
                                    showFutureDailyGoals={
                                        this.state.showFutureDailyGoals
                                    }
                                />
                            )}
                        />
                    </div>
                    <Render id="futureGoalsToggle" when={true}>
                        <ToggleFutureDailyGoals
                            {...{
                                goalStreakInfos: this.props.GoalStreakInfos,
                                DailyGoals: this,
                            }}
                        />
                    </Render>
                    <Render
                        id="postLink"
                        when={
                            !this.state.isPosting &&
                            this.state.checkedGoals.length
                        }
                    >
                        <LogDailyGoals DailyGoals={this} />
                    </Render>
                    <Render id="postingText" when={this.state.isPosting}>
                        Please wait...
                    </Render>
                </Render>
            </div>
        );
    }
}

function DailyGoalsLoadingSkeleton({ rowCount }: { rowCount: number }) {
    return (
        <div className={styles.skeletonList} aria-hidden={true}>
            {Array.from({ length: rowCount }).map((_, index) => (
                <DailyGoalsLoadingSkeletonRow
                    key={index}
                    isShort={index % 3 === 2}
                />
            ))}
        </div>
    );
}

function DailyGoalsLoadingSkeletonRow({ isShort }: { isShort: boolean }) {
    return (
        <div
            className={classNames(styles.skeletonRow, {
                [styles.skeletonRowShort]: isShort,
            })}
        >
            <span className={styles.skeletonIcon} />
            <span className={styles.skeletonName} />
            <span className={styles.skeletonPlus} />
            <span className={styles.skeletonMetric} />
        </div>
    );
}
